import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/docs/plugins/capabilities")({
	component: CapabilitiesPage,
});

function H({ id, children }: { id: string; children: React.ReactNode }) {
	return (
		<h2
			id={id}
			className="text-base font-semibold text-zinc-900 dark:text-zinc-100 mt-14 mb-4 pt-6 border-t border-zinc-100 dark:border-zinc-800/50 scroll-mt-20 first:border-0 first:mt-0 first:pt-0"
		>
			{children}
		</h2>
	);
}

function H3({ children }: { children: React.ReactNode }) {
	return (
		<h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 mt-6 mb-2">
			{children}
		</h3>
	);
}

function Pre({ title, children }: { title?: string; children: string }) {
	return (
		<div className="rounded bg-[#0a0a0a] border border-zinc-800 overflow-hidden mb-4">
			{title && (
				<div className="px-3 py-1 text-[9px] text-zinc-500 uppercase tracking-widest border-b border-zinc-800 font-medium">
					{title}
				</div>
			)}
			<pre className="px-3 py-2 overflow-x-auto text-[11px] leading-relaxed text-zinc-300 font-mono">
				{children}
			</pre>
		</div>
	);
}

function DocTable({
	headers,
	children,
}: { headers: string[]; children: React.ReactNode }) {
	return (
		<div className="rounded border border-zinc-200 dark:border-zinc-800 mb-4 text-[12px]">
			<Table>
				<TableHeader>
					<TableRow className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/50">
						{headers.map((h) => (
							<TableHead
								key={h}
								className="text-left py-1.5 px-3 text-[10px] text-zinc-400 uppercase tracking-wider font-medium h-auto"
							>
								{h}
							</TableHead>
						))}
					</TableRow>
				</TableHeader>
				<TableBody className="divide-y divide-zinc-100 dark:divide-zinc-800/50">
					{children}
				</TableBody>
			</Table>
		</div>
	);
}

function CapabilitiesPage() {
	const { t } = useTranslation("docs");
	return (
		<div className="w-full">
			<div className="mb-4">
				<h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
					{t("capabilities_title")}
				</h1>
				<p
					className="mt-1.5 text-[13px] text-zinc-500"
					dangerouslySetInnerHTML={{ __html: t("capabilities_intro") }}
				/>
			</div>

			<H id="process">{t("capabilities_process")}</H>
			<ol className="text-[12px] text-zinc-500 space-y-1.5 list-decimal list-inside mb-4">
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("capabilities_declaration")}
					</strong>{" "}
					—{" "}
					<span
						dangerouslySetInnerHTML={{
							__html: t("capabilities_declaration_desc"),
						}}
					/>
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("capabilities_approval")}
					</strong>{" "}
					— {t("capabilities_approval_desc")}
				</li>
				<li>
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("capabilities_runtime_check")}
					</strong>{" "}
					—{" "}
					<span
						dangerouslySetInnerHTML={{
							__html: t("capabilities_runtime_check_desc"),
						}}
					/>
				</li>
			</ol>

			<H id="available">{t("capabilities_available")}</H>

			<H3>
				<code className="text-[11px] font-mono">db_read</code> —{" "}
				{t("capabilities_db_read")}
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("capabilities_db_read_desc")}
			</p>
			<Pre title="plugin.toml">{`[capabilities]
db_read = ["product", "metal_product_spec"]`}</Pre>
			<Pre title="rust">{`let rows = db_query(
    "SELECT id, title FROM product WHERE id = $1",
    vec![json!(product_id)],
)?;`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">db_write</code> —{" "}
				{t("capabilities_db_write")}
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("capabilities_db_write_desc")}
			</p>
			<Pre title="plugin.toml">{`[capabilities]
db_write = ["metal_product_spec"]`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">http_request</code> —{" "}
				{t("capabilities_http")}
			</H3>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("capabilities_http_desc") }}
			/>
			<Pre title="plugin.toml">{`[capabilities]
http_request = ["api.openai.com", "*.example.com"]`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">ai_complete</code> —{" "}
				{t("capabilities_ai")}
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("capabilities_ai_desc")}
			</p>
			<Pre title="plugin.toml">{`[capabilities]
ai_complete = true`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">kv</code> —{" "}
				{t("capabilities_kv")}
			</H3>
			<p className="text-[13px] text-zinc-500 mb-3 leading-relaxed">
				{t("capabilities_kv_desc")}
			</p>
			<Pre title="plugin.toml">{`[capabilities]
kv = true`}</Pre>
			<Pre title="rust">{`kv_set("cache:product:123", json!({"processed": true}))?;
let cached = kv_get("cache:product:123")?;`}</Pre>

			<H3>
				<code className="text-[11px] font-mono">ref_data</code> —{" "}
				{t("capabilities_ref_data")}
			</H3>
			<p
				className="text-[13px] text-zinc-500 mb-3 leading-relaxed"
				dangerouslySetInnerHTML={{ __html: t("capabilities_ref_data_desc") }}
			/>
			<Pre title="plugin.toml">{`[capabilities]
ref_data = ["metal.shapes.ru", "metal.grades.ru"]`}</Pre>
			<Pre title="rust">{`let shape_info = ref_data("metal.shapes.ru", "rebar")?;`}</Pre>

			<H id="limits">{t("capabilities_limits")}</H>
			<DocTable
				headers={[
					t("capabilities_limits_param"),
					t("capabilities_limits_manifest_field"),
					t("capabilities_limits_default"),
				]}
			>
				<TableRow>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("capabilities_limits_memory")}
					</TableCell>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							max_memory_bytes
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">64 MB</TableCell>
				</TableRow>
				<TableRow>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("capabilities_limits_execution")}
					</TableCell>
					<TableCell className="py-1.5 px-3">
						<code className="text-[11px] font-mono font-semibold text-zinc-800 dark:text-zinc-200">
							max_execution_ms
						</code>
					</TableCell>
					<TableCell className="py-1.5 px-3 text-zinc-500">
						{t("capabilities_limits_execution_default")}
					</TableCell>
				</TableRow>
			</DocTable>

			<H id="full-example">{t("capabilities_full_example")}</H>
			<Pre title="plugin.toml">{`[plugin]
id = "com.malevich.metal-ru"
name = "Russian Metal Industry"
version = "1.0.0"
description = "Metal product detection and weight calculation"
author = "Malevich AI"
min_abi_version = "1.0"

[capabilities]
db_read = ["product", "metal_product_spec"]
db_write = ["metal_product_spec"]
kv = true
ref_data = ["metal.shapes.ru", "metal.grades.ru", "metal.dimensions.ru"]
max_memory_bytes = 67108864
max_execution_ms = 10000

[[extensions]]
point = "enrichment.extract"
handler = "extract_metal_spec"
priority = 100

[[extensions]]
point = "enrichment.calculate"
handler = "calculate_metal_weight"
priority = 100`}</Pre>

			<div className="mt-14 pt-4 border-t border-zinc-100 dark:border-zinc-800/50">
				<p className="text-[13px] text-zinc-500">
					<strong className="text-zinc-900 dark:text-zinc-100">
						{t("capabilities_next")}
					</strong>{" "}
					<Link
						to="/docs/plugins/publishing"
						className="text-primary hover:underline"
					>
						{t("publishing")}
					</Link>{" "}
					— {t("capabilities_next_publishing")}
				</p>
			</div>
		</div>
	);
}
